import {Repository} from "./Repository";
import {AxiosInstance} from "axios";
import {User} from "../models/User";
import {UserProfile} from "../models/UserProfile";
import {OwnProfile} from "../models/OwnProfile";
import {UserProfileInterface} from "../models/interfaces/UserProfileInterface";
import {UserInterface} from "../models/interfaces/UserInterface";
import {QueryParametersInterface} from "@api/repositories/interfaces/QueryParametersInterface";
import {OnlineQueryParametersInterface} from "@api/repositories/interfaces/OnlineQueryParametersInterface";
import {ProfileField} from "../../types/ProfileField";
import { UserCreateParams } from "@api/models/interfaces/user/UserCreateParams";

export class UserRepository implements Repository {
    client: AxiosInstance;

    constructor(client: AxiosInstance) {
        this.client = client;
    }

    ownProfile(fields: Array<ProfileField> = [], lang?: string): Promise<UserProfile|null> {
        return this.client.get<UserProfileInterface>(`/users/me/profile`, lang ? { headers: { "X-Appercode-Language": lang } } : undefined)
            .then(({data}) => {
                if (!data) { return null; }

                const profile =  new UserProfile(data);

                fields.map(field => {
                    if (field.name in data) {
                        profile[field.name] = data[field.name];
                    }
                })

                return profile;
            });
    }

    saveOwnProfile(profile: UserProfile, fields: Array<ProfileField> = []): Promise<UserProfile|null> {
        return this.client.put<UserProfileInterface>(`/users/me/profile`, new OwnProfile(profile, fields))
            .then(({data}) => {
                return data ? new UserProfile(data) : null;
            });
    }

    profile(userId: number, include: string[] = []): Promise<UserProfile|null> {
        return this.client.post<UserProfileInterface[]>(`/objects/UserProfiles/query`, {
            take: 1,
            where: { userId: userId },
            include
        })
            .then(({data}) => {
                return data?.length > 0 ? new UserProfile(data[0]) : null;
            });
    }

    profiles(query: QueryParametersInterface = {}, config = {}) {
        return this.client.post<UserProfileInterface[]>(`/objects/UserProfiles/query`, query, config)
            .then(({data}) => {
                return data.map(item => new UserProfile(item))
            })
    }

    profilesWithCount(query: QueryParametersInterface = {}, config = {}): Promise<{profiles: UserProfile[]; count: number}> {
        return this.client.post<UserProfileInterface[]>(`/objects/UserProfiles/query`, query, {
            ...config,
            params: {count: true}
        })
            .then(({data, headers}) => {
                return {profiles: data.map(item => new UserProfile(item)), count: parseInt(headers['x-appercode-totalitems'] || 0)}
            })
    }

    saveProfile(profile: UserProfile) {
        return this.client.put('/objects/UserProfiles/' + profile.id, profile);
    }

    user(id: number): Promise<User|null> {
        return this.client.get<UserInterface>('users/' + id)
            .then(({data}) => {
                return data ? new User(data) : null;
            });
    }

    saveUser(id: number, changes: UserInterface) {
        return this.client.put('/users/' + id, changes);
    }

    changePassword(id: number, password: string, oldPassword: string) {
        return this.client.put('/users/' + id + '/changePassword', {
            'oldPassword': oldPassword,
            'newPassword': password
        })
    }

    activity(ids: number[], activity: 'last' | 'first') {
        return this.client.post<Record<number, string>[]>(`/users/activity/query`, ids, {
            params: {
                activity
            }
        })
            .then(({data}) => {
                return data;
            })
    }

    online(query: OnlineQueryParametersInterface[], activity: string[]) {
        return this.client.post<Record<string, { count: number; userIds: number[] }>>(`/users/online/query`, { query, activity })
            .then(({data}) => {
                return data;
            })
    }

    profileUpdateInfo(ids: string[]) {
        const query = {
            where: {
                id: {
                    $in: ids
                }
            },
            include: ['id', 'updatedAt'],
            skip: 0,
            take: -1
        }
        return this.client.post<Record<'id'|'updatedAt', string>[]>(`/objects/UserProfiles/query`, query)
            .then(({data}) => {
                return data;
            })
    }

    create(item: UserCreateParams): Promise<User> {
        return this.client.post<User>(`/users`, item)
            .then(({ data }) => {
                return data;
            })
    }

    createBatch(items: UserCreateParams[]): Promise<User[]> {
        return this.client.post<User[]>(`/users/batch`, items)
            .then(({ data }) => {
                return data;
            })
    }
}